import { useCallback } from "react";
import type { FunctionComponent, ReactNode } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { TopPicksWidgetItem } from "~/components/article/article-left-hand-side-widgets/top-picks-widget/top-picks-widget-item";
import { useTrackImpressionEventByIntersection } from "~/components/tracking";
import type { TopPicksEvents } from "~/components/tracking/google-analytics/types";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import type { AiTopPicksResponse } from "~/lib/ai-engine/types";
import { getAbsoluteUrl, sortArrayByReferenceOrder } from "~/lib/utils";
import type { topPicksWidgetDisplayArticleQuery } from "~/queries/__generated__/topPicksWidgetDisplayArticleQuery.graphql";

import { useTracking } from "./hooks";
import { Container, Header } from "./styles";

export type Props = {
  aiData: AiTopPicksResponse;
  className?: string;
  maximumHeight: number | undefined;
  slots?: {
    footer?: { estimatedHeight: number; node: ReactNode };
    header?: { estimatedHeight: number; node: ReactNode };
  };
};

export const TopPicksWidgetDisplay: FunctionComponent<Props> = ({
  aiData,
  className,
  maximumHeight,
  slots,
}) => {
  const { trackClick } = useTracking();

  const data = useLazyLoadQuery<topPicksWidgetDisplayArticleQuery>(
    graphql`
      query topPicksWidgetDisplayArticleQuery($entityIds: [String], $first: Int!) {
        articles(filter: { entityIds: $entityIds }, first: $first) {
          edges {
            node {
              ...topPicksWidgetItemArticle
              entityId
              urlAlias
            }
          }
        }
      }
    `,
    {
      entityIds: aiData.results.map(result => result.id) ?? [],
      first: aiData.results.length ?? 0,
    },
    { fetchPolicy: "store-or-network" },
  );

  const orderedArticles = sortArrayByReferenceOrder({
    data: { array: data.articles.edges ?? [], idGetter: item => item.node.entityId },
    order: { array: aiData.results, idGetter: item => item.id },
  });

  const recommendationId = aiData.rec_id;
  const topPicksEntityIds = aiData.results.map(({ id }) => id);

  const impressionEvent = useCallback<() => TopPicksEvents>(
    () => ({
      action: `Top Picks/inHouse/${recommendationId}/Impression`,
      category: "Top Picks",
      label: topPicksEntityIds.join(","),
    }),
    [recommendationId, topPicksEntityIds],
  );

  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "article",
        recommendation_id: recommendationId,
        widget_name: "top_picks",
      },
      subcategory: "widget",
    }),
    [recommendationId],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection({
    getEvent: impressionEvent,
    getGa4Event: ga4ImpressionEvent,
    options: {
      intersectionThreshold: [0.1],
      isSendGA4Tracking: true,
      isSendGATracking: true,
      shouldSendOnce: true,
      shouldThrottle: true,
    },
  });

  if (orderedArticles.length <= 0 || maximumHeight === undefined) return null;

  const limitedItemCount = Math.max(
    Math.round(
      (maximumHeight -
        (slots?.header?.estimatedHeight ?? 0) -
        (slots?.footer?.estimatedHeight ?? 0)) /
        250,
    ),
    1,
  );

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <Header>Top Picks</Header>
      {slots?.header?.node}
      {orderedArticles.slice(0, limitedItemCount).map(({ node }, index) => (
        <TopPicksWidgetItem
          key={node.entityId}
          reference={node}
          onClick={() =>
            trackClick(aiData.rec_id, node.entityId, getAbsoluteUrl(node.urlAlias), `${index + 1}`)
          }
        />
      ))}
      {slots?.footer?.node}
    </Container>
  );
};

TopPicksWidgetDisplay.displayName = "TopPicksWidgetDisplay";
